import React, { Fragment, useContext } from 'react';
import { useQuery } from '@apollo/client';
import dayjs from 'op-utils/dayjs';

import { isDemo } from 'op-utils';

import UserContext, { UserContextType } from 'op-contexts/UserContext/UserContext';
import { ROPatientContext } from 'op-pages/RO/PatientSummary/context';
import PhotoWidget from 'shared-components/components/UIFormComponents/PhotoWidget';

import { GET_PATIENT_PROFILE, GET_RO_PATIENT_CONTEXT } from './PatientCardQueries';
import PSOPatientCard from './PSOPatientCard';
import ROPatientCard from './ROPatientCard';
import { Props, SimpleCardProps, DetailedCardProps, PatientProfileQueryResultData } from './types';
import SimplifiedPatientCard from './SimplifiedPatientCard';
import { calculateMomentAge } from 'shared-components/utils';
import { PAT_ID_1_REFRESH_TIME } from 'shared-components/enums';
import { patientGenderStockImage, formatPatientName } from 'op-utils/PatientHelper';
import useInterval from 'shared-components/utils/useInterval';
import { Typography, Stack, Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import { PermissionType } from 'op-enums';

const DEFAULT_PROFILE = {
  firstName: '',
  middleName: '',
  lastName: '',
  fullName: '',
  preferredName: '',
  ida: '',
  gpFullName: '',
  dob: '',
  patientGender: '',
  address: {
    line1: '',
    line2: '',
    city: '',
    state: '',
    country: '',
    postcode: '',
    formattedAddress: '',
    ukFormattedAddress: '',
  },
  allergy: [],
  carepathPatientDiagnosis: [],
  healthMedicareNumber: '',
  healthMedicareIrn: '',
  medicareExpiryRawYear: '',
  medicareExpiryRawMonth: '',
  secondaryPhone: '',
  primaryPhone: '',
  patientState: '',
  pxOptedIn: '',
  horizonCenterId: '',
  email: '',
  pxOptOutComment: '',
  tags: [],
  emrPatientId: '',
  emrInstance: '',
  userProfile: {
    hasPatId1: true,
  },
};

const roleComponentMap = {
  ro: ROPatientCard,
  clinicstaff: ROPatientCard,
  nurse: PSOPatientCard,
  roSimplified: SimplifiedPatientCard,
  pso: PSOPatientCard,
};

const renderDetailedCard = (props: DetailedCardProps) => {
  const { patientCardView, showMoreDetails, moreDetailsClickHandler } = props;
  if (props.primaryRole) {
    const viewKey = patientCardView ? patientCardView : props.primaryRole;
    const Card = roleComponentMap[viewKey as keyof typeof roleComponentMap] ?? ROPatientCard;
    return (
      <div style={{ position: 'relative', width: '100%' }}>
        <Card {...props} />
      </div>
    );
  }
  return <Fragment />;
};

const SimpleCard = ({ attributes }: SimpleCardProps): JSX.Element => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isVerySmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const address = attributes?.address?.ukFormattedAddress || '-';

  return (
    <Box
      sx={{
        boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.2)',
        position: 'relative',
        width: '100%',
        padding: isVerySmallScreen ? '8px 12px' : '12px 16px',
        marginBottom: '1px',
        overflowX: 'auto',
        minHeight: 'fit-content',
        height: 'auto',
      }}>
      <Grid
        container
        spacing={isVerySmallScreen ? 1 : isSmallScreen ? 2 : 4}
        sx={{
          alignItems: 'flex-start',
          minWidth: 'fit-content',
          flexWrap: 'nowrap',
          minHeight: 'fit-content',
        }}>
        <Grid
          item
          sx={{ display: 'flex', alignItems: 'center', gap: isVerySmallScreen ? '4px' : '8px', flexShrink: 0 }}>
          <PhotoWidget url={attributes.photoUrl} patientId={attributes.patientId} enableOverlay={true} size={'reg'} />
          <Stack direction="column" sx={{ minWidth: isVerySmallScreen ? '120px' : '150px' }}>
            <Typography data-testid="patient-name-block" variant="subtitle1">
              <strong>{attributes.fullName}</strong>
            </Typography>
            <Typography variant="body2">
              EMR: <strong>{attributes.emrInstance || '-'}</strong> | Patient ID:{' '}
              <strong>{attributes.ida || '-'}</strong> | Sex: <strong>{attributes.gender || '-'}</strong>
            </Typography>
          </Stack>
        </Grid>
        <Grid item sx={{ flexShrink: 0, minWidth: isVerySmallScreen ? '100px' : '150px' }}>
          <Stack direction="column">
            <Typography variant="body2">Date of birth</Typography>
            <Typography variant="body2">
              <strong>{attributes.fullAge}</strong>
            </Typography>
          </Stack>
        </Grid>
        <Grid item sx={{ flexShrink: 1, minWidth: isVerySmallScreen ? '120px' : '180px' }}>
          <Stack direction="column">
            <Typography variant="body2">Address</Typography>
            <Typography variant="body2">
              <strong>{address}</strong>
            </Typography>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

const PatientCard = (props: Props): JSX.Element => {
  const { id, simple = true, patientCardView } = props;
  const { setPatientDetails } = useContext(ROPatientContext);
  const { state, hasPermission } = useContext<UserContextType>(UserContext);

  // Use the minimal RO context query for users with radiation workflow access.
  const useRoPatientContextQuery = hasPermission(PermissionType.RADIATION_ACCESS);
  const patientQuery = useRoPatientContextQuery ? GET_RO_PATIENT_CONTEXT : GET_PATIENT_PROFILE;

  const {
    data: patientProfileData,
    loading: patientProfileLoading,
    refetch,
  } = useQuery(patientQuery, {
    context: { handleErrorsLocally: ['Patient matching query does not exist.'] },
    variables: { patientId: id },
    onCompleted: (data: PatientProfileQueryResultData) => {
      setPatientDetails(data?.patient);
    },
    onError: () => {
      document.location.href = '/radiation';
    },
  });

  // Refresh profile every few seconds until patient has pat_id_1
  useInterval(
    () => {
      refetch();
    },
    patientProfileData?.patient?.userProfile?.hasPatId1 ? null : PAT_ID_1_REFRESH_TIME,
  );

  if (!id) return <Fragment />;

  const sharedProps = {
    id,
    roPatient: patientProfileData?.patient || DEFAULT_PROFILE,
  };
  const formattedDateOfBirth = sharedProps.roPatient.dob
    ? dayjs(sharedProps.roPatient.dob).format(CurrentAppConfig.PatientCard.dateOfBirthFormat)
    : '-';
  const calculatedAge = sharedProps.roPatient.dob ? calculateMomentAge(dayjs(sharedProps.roPatient.dob)) : '-';

  const attributes: any = {};
  attributes.dob = sharedProps.roPatient.dob;
  attributes.emrInstance = sharedProps.roPatient.emrInstance;
  attributes.fullAge =
    formattedDateOfBirth !== '-' ? `${formattedDateOfBirth} (Age ${calculatedAge})` : formattedDateOfBirth;
  attributes.address = sharedProps.roPatient.address;

  attributes.fullName = formatPatientName(
    sharedProps.roPatient.firstName,
    sharedProps.roPatient.middleName,
    sharedProps.roPatient.preferredName,
    sharedProps.roPatient.lastName,
    sharedProps.roPatient.fullName,
  );
  attributes.gender = sharedProps.roPatient.patientGender;
  attributes.ida =
    sharedProps.roPatient.ida && sharedProps.roPatient.ida.replace(/ /g, '') !== '' ? sharedProps.roPatient.ida : '-';
  attributes.allergy = sharedProps.roPatient.allergy;
  attributes.carepathPatientDiagnosis = sharedProps.roPatient.carepathPatientDiagnosis.filter(
    (item: any) => item.emrDiagnosisKey !== null,
  );
  attributes.photoUrl = isDemo ? patientGenderStockImage(sharedProps.roPatient.patientGender) : null;
  attributes.patientId = id;
  attributes.mobile = sharedProps.roPatient.primaryPhone || '-';
  attributes.landline = sharedProps.roPatient.secondaryPhone || '-';
  attributes.pxOptedIn = sharedProps.roPatient.pxOptedIn;
  attributes.horizonCenterId = sharedProps.roPatient.horizonCenterId;
  attributes.tags = sharedProps.roPatient.tags;
  attributes.selectedPatientOptIn = {
    id: id,
    firstName: sharedProps.roPatient.firstName,
    lastName: sharedProps.roPatient.lastName,
    pxOptedIn: sharedProps.roPatient.pxOptedIn,
    primaryPhone: sharedProps.roPatient.primaryPhone,
    email: sharedProps.roPatient.email,
    pxOptOutComment: sharedProps.roPatient.pxOptOutComment,
    emrInstance: sharedProps.roPatient.emrInstance,
  };
  attributes.emrPatientId = sharedProps.roPatient.emrPatientId;
  attributes.emrInstance = sharedProps.roPatient.emrInstance;
  attributes.hasPatId1 = sharedProps.roPatient.userProfile.hasPatId1;

  const detailedCardProps = {
    id: props.id,
    moreDetailsClickHandler: props.moreDetailsClickHandler,
    showMoreDetails: props.showMoreDetails,
    primaryRole: state.primaryRole,
    attributes: attributes,
    loading: patientProfileLoading,
    patientCardView: patientCardView,
  };

  return simple ? <SimpleCard attributes={attributes} /> : renderDetailedCard(detailedCardProps);
};

export default PatientCard;
